<template>
  <v-container fluid class="pa-2">
    <!-- ===================== DAILY TRADES (NEU) ===================== -->
    <v-card variant="outlined" class="mb-6">
      <v-card-title class="d-flex align-center justify-space-between">
        <span>📌 Daily Trades Empfehlungen</span>

        <div class="d-flex align-center ga-2">
          <span v-if="dailyTab !== 'products'" class="text-caption">
            Stand: <strong>{{ dailyTradesDate }}</strong>
            <span v-if="dailyTradesSource">· Quelle: {{ dailyTradesSource }}</span>
          </span>

          <v-btn size="small" variant="text" :loading="dailyTradesLoading" @click="loadDailyTrades">
            Refresh
          </v-btn>
        </div>
      </v-card-title>

<v-card-text>
  <v-alert v-if="dailyTradesErrorText && dailyTab !== 'products'" type="error" variant="tonal" class="mb-4">
    {{ dailyTradesErrorText }}
  </v-alert>

  <v-tabs v-model="dailyTab" density="compact" class="mb-3">
    <v-tab value="suggestions">Aktien</v-tab>
    <v-tab value="products">ETF / Rohstoffe</v-tab>
    <v-tab value="all">Alle Stocks</v-tab>
  </v-tabs>

  <v-window v-model="dailyTab">
    <v-window-item value="products">
      <product-rankings-card ref="productRankings" />
    </v-window-item>
    <!-- TAB 1: Empfehlungen -->
    <v-window-item value="suggestions">
      <div v-if="dailyTradesLoading" class="text-caption py-2">Lade Empfehlungen…</div>

      <v-row v-else>
        <!-- V2 recommendations -->
        <v-col v-for="group in dailyRecommendationGroups" :key="group.key" cols="12" lg="6">
          <div class="text-subtitle-2">{{ group.title }}</div>
          <div class="text-caption text-medium-emphasis mb-2">{{ group.description }}</div>

          <div v-if="group.loading" class="text-caption py-2">Lade Empfehlungen…</div>
          <v-alert v-else-if="group.error" type="error" variant="tonal" class="mb-2">
            {{ group.error }}
          </v-alert>
          <v-alert v-else-if="!group.rows.length" type="info" variant="tonal" class="mb-2">
            {{ group.empty }}
          </v-alert>
          <template v-else>
            <v-card
              v-for="(t, idx) in group.rows"
              :key="group.key + dailyTradeKey(t)"
              class="mb-2 daily-trade-card daily-trade-card-v2"
              @click="goToAnalyzerFromDailyTrade(t)"
            >
              <v-card-text class="py-3">
                <div class="d-flex align-center flex-wrap ga-2">
                  <v-chip size="small" :color="group.key === 'sell' ? 'red-lighten-3' : 'light-blue-lighten-3'">{{ group.key === 'sell' ? 'Flop' : 'Top' }} {{ idx + 1 }}</v-chip>
                  <strong class="text-body-1">{{ t.stocks_name }}</strong>
                  <v-chip v-if="getDailyTradeStrategyTag(t)" size="x-small"
                    :color="getDailyTradeStrategyTag(t).color"
                    :variant="getDailyTradeStrategyTag(t).variant">{{ getDailyTradeStrategyTag(t).label }}</v-chip>
                </div>
                <div class="text-subtitle-1 mt-2">
                  {{ group.key === 'sell' ? 'Sell-Score' : 'Buy-Score V2' }}:
                  <strong>{{ formatBuyScore(group.key === 'sell' ? (t.sell_score ?? t.score) : t.buy_score_v2) }}</strong>
                  <span class="text-no-wrap ml-4">RSL (26 Wochen): <strong>{{ formatRsl(t.rsl_26w) }}</strong></span>
                </div>
                <div class="text-caption mt-1" :title="momentumTooltip(t)">
                  Momentum (20 Handelstage): <strong>{{ formatMomentum(t.momentum_20_pct, '%') }}</strong>
                  · Änderung (5 Handelstage): <strong>{{ formatMomentum(t.momentum_change_5_pp, 'PP') }}</strong>
                  <div>{{ t.momentum_status || '—' }}</div>
                  <div v-if="t.momentum_cross">{{ t.momentum_cross }}</div>
                </div>
              </v-card-text>
            </v-card>
          </template>
        </v-col>
      </v-row>
    </v-window-item>

    <!-- TAB 2: Alle Stocks -->
    <v-window-item value="all">
      <div v-if="dailyAllLoading" class="text-caption py-2">Lade Tabelle…</div>

      <v-alert v-else-if="dailyAllErrorText" type="error" variant="tonal" class="mb-3">
        {{ dailyAllErrorText }}
      </v-alert>

      <v-data-table
        v-else
        class="elevation-1 text-caption"
        density="compact"
        fixed-header
        height="70vh"
        :headers="dailyAllHeaders"
        :items="dailyAllRows"
        :items-per-page="25"
        item-key="stocks_name"
        @click:row="onClickDailyAllRow"
      >
        <template #item.buy_candidate="{ item }">
          <span :class="item.buy_candidate ? 'text-green-lighten-1' : 'text-medium-emphasis'">
            {{ item.buy_candidate ? "BUY" : "-" }}
          </span>
        </template>

        <template #item.sell_candidate="{ item }">
          <span :class="item.sell_candidate ? 'text-red-lighten-1' : 'text-medium-emphasis'">
            {{ item.sell_candidate ? "SELL" : "-" }}
          </span>
        </template>

        <template #item.why_buy="{ item }">
          <span class="text-medium-emphasis">{{ (item.why_buy || []).join(", ") }}</span>
        </template>

        <template #item.rsl_26w="{ item }">
          <span class="text-no-wrap">{{ formatRsl(item.rsl_26w) }}</span>
        </template>

        <template #item.momentum_20_pct="{ item }">
          <span class="text-no-wrap" :title="momentumTooltip(item)">{{ formatMomentum(item.momentum_20_pct, '%') }}</span>
        </template>

        <template #item.buy_score_v2="{ item }">
          <span class="text-no-wrap">{{ formatBuyScore(item.buy_score_v2) }}</span>
        </template>

        <template #item.buy_score_v2_delta="{ item }">
          <span class="text-no-wrap" title="Buy-Score V2 minus bisheriger Buy-Score">{{ formatBuyScore(item.buy_score_v2_delta, true) }}</span>
        </template>

        <template #item.momentum_change_5_pp="{ item }">
          <span class="text-no-wrap" :title="momentumTooltip(item)">{{ formatMomentum(item.momentum_change_5_pp, 'PP') }}</span>
        </template>

        <template #item.momentum_status="{ item }">
          <div :title="momentumTooltip(item)">
            <div>{{ item.momentum_status || '—' }}</div>
            <div v-if="item.momentum_cross" class="text-caption">{{ item.momentum_cross }}</div>
          </div>
        </template>

        <template #item.why_sell="{ item }">
          <span class="text-medium-emphasis">{{ (item.why_sell || []).join(", ") }}</span>
        </template>
      </v-data-table>
    </v-window-item>
  </v-window>
</v-card-text>

    </v-card>
    <!-- ===================== /DAILY TRADES (NEU) ===================== -->

    <v-row dense>
      <v-col
        v-for="(r, idx) in sortedRankings"
        :key="(r && r.rankingURL) ? r.rankingURL : ('rk-' + idx)"
        cols="12"
        sm="6"
        md="4"
        class="d-flex"
      >
        <v-card class="w-100" elevation="2">
          <v-card-title class="text-subtitle-2 py-2" :class="headerClass(idx, compareCount)">
            {{ titleFromUrl(r?.rankingURL || '') }}
          </v-card-title>

          <v-img
            :src="bust(r && typeof r.rankingURL === 'string' ? r.rankingURL : '')"
            :aspect-ratio="imgAspect(r.rankingURL)"
            :height="imgHeight(r.rankingURL)"
            contain
            class="chart-img"
            @click="openFile(r.rankingURL)"
          />
        </v-card>
      </v-col>
    </v-row>

    <!-- ✅ große Darstellung NUR für rating.png -->
    <div v-if="ratingItem" class="my-4">
      <v-card class="mx-auto" title="Ranking (groß)" color="orange" />

      <v-img
          v-if="ratingItem && typeof ratingItem.rankingURL === 'string'"
            :src="bust(ratingItem.rankingURL)"
        :aspect-ratio="3 / 4"
        contain
        class="bg-grey-lighten-2 ranking-img-large"
        @click="openFile(ratingItem.rankingURL)"
      />
    </div>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import ProductRankingsCard from "./ProductRankingsCard.vue";

export default {
  name: "RankingScreen",
  components: { ProductRankingsCard },

  async mounted() {
    console.log("bin in RankingScreen");
    this.$store.dispatch("getRanking").catch((e) => console.error(e));
    this.cacheBuster = Date.now();  // 👈 neu
    this.loadDailyTrades();

    // ✅ wichtig: damit wir idstocks wie im Dashboard ermitteln können
    // (falls stocks noch nicht geladen sind)
    try {
      await this.$store.dispatch("postStocks");
    } catch (e) {
      // falls action nicht existiert oder schon geladen ist -> nicht hart crashen
      console.warn("postStocks konnte nicht geladen werden (evtl. nicht nötig):", e?.message || e);
    }
  },
  data() {
  return {
    dailyTab: "suggestions",
    cacheBuster: Date.now(),   // 👈 neu
  };
},

  computed: {
    ...mapGetters({
      rankings: "stateRankings",
      // Daily Trades
      dailyTrades: "stateDailyTrades",
      dailyTradesLoading: "stateDailyTradesLoading",
      dailyTradesError: "stateDailyTradesError",
      // Stocks (für idstocks Lookup)
      stocks: "stateStocks",
      preisdeltasAll: "statePreisdeltasAll",
      dailyAll: "stateDailyTradesAll",
      dailyAllLoading: "stateDailyTradesAllLoading",
      dailyAllError: "stateDailyTradesAllError",
    }),

dailyAllErrorText() {
  const e = this.dailyAllError;
  if (!e) return null;
  if (e?.response?.data) return typeof e.response.data === "string" ? e.response.data : JSON.stringify(e.response.data);
  if (e?.message) return String(e.message);
  return String(e);
},

dailyAllRows() {
  return Array.isArray(this.dailyAll?.rows) ? this.dailyAll.rows : [];
},

dailyTopV2() {
  const sorted = this.dailyAllRows
    .filter(row => row && row.buy_score_v2 != null && row.buy_score_v2 !== ''
      && Number.isFinite(Number(row.buy_score_v2)))
    .slice()
    .sort((a, b) => Number(b.buy_score_v2) - Number(a.buy_score_v2));
  const seen = new Set();
  return sorted.filter(row => {
    const key = this.dailyTradeKey(row);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, 6);
},

dailyRecommendationGroups() {
  const allByKey = new Map(this.dailyAllRows.map(row => [this.dailyTradeKey(row), row]));
  const sells = this.dailySells.map(row => ({ ...allByKey.get(this.dailyTradeKey(row)), ...row }));
  return [
    { key: 'buy', title: 'BUY · Top 6', description: 'Nach Buy-Score V2 sortiert', rows: this.dailyTopV2,
      loading: this.dailyAllLoading, error: this.dailyAllErrorText, empty: 'Noch keine V2-Scores vorhanden.' },
    { key: 'sell', title: 'SELL', description: 'Verkaufsempfehlungen', rows: sells,
      loading: false, error: null, empty: 'Keine SELL-Vorschläge vorhanden.' },
  ];
},

dailyAllHeaders() {
  return [
    { title: "Name", key: "stocks_name", align: "start" },
    { title: "BUY?", key: "buy_candidate", align: "end" },
    { title: "SELL?", key: "sell_candidate", align: "end" },
    { title: "buy_score", key: "buy_score", align: "end" },
    { title: "Buy-Score V2", key: "buy_score_v2", align: "end" },
    { title: "Score-Differenz", key: "buy_score_v2_delta", align: "end" },
    { title: "sell_score", key: "sell_score", align: "end" },
    { title: "RSL (26 Wochen)", key: "rsl_26w", align: "end" },
    { title: "Momentum (20 Handelstage) %", key: "momentum_20_pct", align: "end" },
    { title: "Momentumänderung (5 Handelstage) PP", key: "momentum_change_5_pp", align: "end" },
    { title: "Momentum-Status", key: "momentum_status", align: "start" },
    { title: "slope_30", key: "slope_30", align: "end" },
    { title: "last30 %", key: "last_30_pct", align: "end" },
    { title: "vol_30", key: "vol_30", align: "end" },
    { title: "accel", key: "accel", align: "end" },
    { title: "Jahr %", key: "perf_jahr", align: "end" },
    { title: "Monat %", key: "perf_monat", align: "end" },
    { title: "Wert", key: "wert", align: "end" },
    { title: "Menge", key: "menge", align: "end" },
    { title: "Kurs", key: "kurs_now", align: "end" },
    { title: "Stoploss", key: "stoploss", align: "end" },
    { title: "25%-Limit", key: "sell25", align: "end" },
    { title: "why_buy", key: "why_buy", align: "start" },
    { title: "why_sell", key: "why_sell", align: "start" },
  ];
},

    // ---------------- DAILY TRADES (NEU) ----------------
    dailyTradesErrorText() {
      const e = this.dailyTradesError;
      if (!e) return null;
      if (e?.response?.data)
        return typeof e.response.data === "string" ? e.response.data : JSON.stringify(e.response.data);
      if (e?.message) return String(e.message);
      return String(e);
    },

    dailyTradesDate() {
      const dt = this.dailyTrades?.generated_at;
      if (!dt) return "—";
      return String(dt).slice(0, 19).replace("T", " ");
    },

    dailyTradesSource() {
      return this.dailyTrades?.source || "";
    },

    dailyBuys() {
      return Array.isArray(this.dailyTrades?.buys) ? this.dailyTrades.buys : [];
    },

    dailySells() {
      return Array.isArray(this.dailyTrades?.sells) ? this.dailyTrades.sells : [];
    },

    // ✅ Lookup: stocks_name -> idstocks (genau wie row.item.idstocks im Dashboard)
    stockIdByName() {
      const map = new Map();
      const list = Array.isArray(this.stocks) ? this.stocks : [];

      // nimm den ersten Treffer pro Name
      for (const s of list) {
        const name = s?.stocks_name;
        const id = s?.idstocks;
        if (name && id != null && !map.has(name)) map.set(name, id);
      }

      return map;
    },

    strategyByStockId() {
      const rows = Array.isArray(this.preisdeltasAll) ? this.preisdeltasAll : [];

      return rows.reduce((acc, row) => {
        const stockId = Number(row?.stock_id);
        if (Number.isFinite(stockId)) acc[stockId] = row;
        return acc;
      }, {});
    },
    // ---------------------------------------------------

    // ✅ rating.png immer als erstes, danach compare index14..01, Rest ans Ende
sortedRankings() {
  const raw = Array.isArray(this.rankings) ? this.rankings : [];

  // ✅ nur gültige Elemente behalten
  const arr = raw.filter(x => x && typeof x.rankingURL === "string" && x.rankingURL.length > 0);

  const isRating = (url) => (url || "").includes("rating.png");
  const isCompare = (url) => (url || "").includes("_compare");

  const getIndexNumber = (url) => {
    if (!url) return -1;
    const file = url.split("/").pop() || url;
    const m = file.match(/_index(\d+)/i);
    return m ? parseInt(m[1], 10) : -1;
  };

  const rating = arr.filter((x) => isRating(x.rankingURL));
  const categoryRating = arr.find((x) => this.isCategoryRating(x.rankingURL)) ||
    (rating.length ? {
      rankingURL: rating[0].rankingURL.replace(/rating\.png(?=[?#]|$)/i, 'rating_rohstoff_etf_fonds.png'),
    } : null);

  const compares = arr
    .filter((x) => !isRating(x.rankingURL) && isCompare(x.rankingURL))
    .sort((a, b) => getIndexNumber(b.rankingURL) - getIndexNumber(a.rankingURL));

  const rest = arr.filter((x) => !isRating(x.rankingURL) && !isCompare(x.rankingURL) &&
    !this.isCategoryRating(x.rankingURL) && !/rating_rohstoff_etf_fonds\.csv(?:[?#]|$)/i.test(x.rankingURL));

  return [...rating, ...(categoryRating ? [categoryRating] : []), ...compares, ...rest];
},

    ratingItem() {
      const arr = Array.isArray(this.rankings) ? this.rankings : [];
      return arr.find((x) => (x.rankingURL || "").includes("rating.png")) || null;
    },

    compareCount() {
      const arr = Array.isArray(this.sortedRankings) ? this.sortedRankings : [];
      const isCompare = (url) => (url || "").includes("_compare");
      const isRating = (url) => (url || "").includes("rating.png");
      return arr.filter((x) => isCompare(x.rankingURL) && !isRating(x.rankingURL)).length;
    },
  },

  methods: {
    onClickDailyAllRow(event, row) {
      const item = row?.item?.raw ?? row?.item ?? row?.internalItem?.raw;
      if (item) this.goToAnalyzerFromDailyTrade(item);
    },

    dailyTradeStockId(item) {
      const id = [item?.idstocks, item?.stocks_idstocks, item?.stock_id]
        .find(value => Number.isFinite(Number(value)) && Number(value) > 0);
      return id != null ? Number(id) : this.stockIdByName.get(item?.stocks_name);
    },

    dailyTradeKey(item) {
      const id = this.dailyTradeStockId(item);
      return id != null ? `stock-${id}` : `name-${String(item?.stocks_name || '').trim().toLowerCase()}`;
    },

    formatBuyScore(value, showSign = false) {
      if (value == null || value === '' || !Number.isFinite(Number(value))) return '—';
      return new Intl.NumberFormat('de-DE', {
        maximumFractionDigits: 6,
        signDisplay: showSign ? 'exceptZero' : 'auto',
      }).format(Number(value));
    },

    formatRsl(value) {
      if (value == null || value === '' || !Number.isFinite(Number(value))) return '—';
      return Number(value).toLocaleString('de-DE', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      });
    },

    formatMomentum(value, unit) {
      if (value == null || value === '' || !Number.isFinite(Number(value))) return '—';
      const formatted = new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        signDisplay: 'exceptZero',
      }).format(Number(value));
      return `${formatted} ${unit}`;
    },

    momentumTooltip(item) {
      const date = item.momentum_as_of;
      if (!date) return 'Kursstand: —';
      const match = String(date).match(/^(\d{4})-(\d{2})-(\d{2})$/);
      return `Kursstand: ${match ? `${match[3]}.${match[2]}.${match[1]}` : date}`;
    },

    async loadDailyTrades() {
      this.$refs.productRankings?.load();
      try {
        await this.$store.dispatch("getDailyTrades");
        await this.$store.dispatch("getDailyTradesAll");
        await this.$store.dispatch("getPreisdeltasAll");
        this.cacheBuster = Date.now(); // 👈 neu
      } catch (e) {
        console.error("getDailyTrades failed", e);
      }
    },
    bust(url) {
      if (typeof url !== "string") return "";   // ✅ NICHT casten!
      if (!url.length) return "";

      const sep = url.indexOf("?") >= 0 ? "&" : "?";
      return `${url}${sep}v=${this.cacheBuster}`;
    },

    // ✅ Klick auf DailyTrades -> Analyzer (idstocks wird aus stateStocks per Name ermittelt)
    goToAnalyzerFromDailyTrade(tradeItem) {
      const name = tradeItem?.stocks_name || "";
      const idstocks = this.dailyTradeStockId(tradeItem);

      if (!idstocks) {
        console.error("Kein idstocks gefunden für stocks_name:", name, "tradeItem:", tradeItem);
        alert(`Kein idstocks gefunden für: "${name}". (stateStocks enthält diesen Namen nicht)`);
        return;
      }

      console.log("go to analyzer with this stock :", idstocks, name);
      this.$router.push({
        name: "Analyzer",
        params: { idstocks, stocks_name: name },
      });
    },

    getDailyTradeAnalysis(tradeItem) {
      const name = String(tradeItem?.stocks_name || "").trim();
      if (!name) return null;
      const stockId = this.dailyTradeStockId(tradeItem);
      if (!stockId) return null;
      return this.strategyByStockId?.[Number(stockId)] || null;
    },

    getDailyTradeStrategyTag(tradeItem) {
      const analysis = this.getDailyTradeAnalysis(tradeItem);
      if (!analysis) return null;

      const winner = String(analysis?.better_strategy || "");
      const isLowQuality = String(analysis?.result_quality || "").toLowerCase() === "low";

      if (winner === "actual_strategy") {
        return {
          label: isLowQuality ? "Aktive Strategie besser ?" : "Aktive Strategie besser",
          color: "success",
          variant: isLowQuality ? "outlined" : "flat",
        };
      }

      if (winner === "buy_and_hold_benchmark") {
        return {
          label: isLowQuality ? "Buy and Hold besser ?" : "Buy and Hold besser",
          color: "warning",
          variant: isLowQuality ? "outlined" : "flat",
        };
      }

      if (winner === "tie") {
        return {
          label: "Gleichstand",
          color: "info",
          variant: "outlined",
        };
      }

      return null;
    },

    openFile(url) {
          // ✅ WICHTIG: nicht die nackte URL öffnen, sonst Browser-Cache!
            const finalUrl = this.bust(url);
            if (!finalUrl) return;
            window.open(finalUrl, "_blank", "noopener");
    },

    isCompare(url) {
      return (url || "").includes("_compare");
    },

    isRating(url) {
      return (url || "").includes("rating.png") || this.isCategoryRating(url);
    },

    isCategoryRating(url) {
      return /rating_rohstoff_etf_fonds\.png(?:[?#]|$)/i.test(url || '');
    },

    imgAspect(url) {
      if (this.isRating(url)) return 3 / 4;
      if (this.isCompare(url)) return 16 / 9;
      return 1;
    },

    imgHeight(url) {
      if (this.isRating(url)) return 520;
      if (this.isCompare(url)) return 260;
      return 320;
    },

    titleFromUrl(url) {
      if (this.isCategoryRating(url)) return "Ranking Rohstoff / ETF / Fonds";
      if (this.isRating(url)) return "Ranking (rating.png)";
      const name = (url || "").split("/").pop() || "";
      const m = name.match(/_index(\d+)/i);
      return m ? `Last 30 days index${m[1].padStart(2, "0")}` : "Last 30 days";
    },

    headerClass(idx, compareTotal) {
      const item = this.sortedRankings[idx];
      const url = item?.rankingURL || "";

      if (this.isRating(url)) return "hdr-default";
      if (!this.isCompare(url)) return "hdr-default";

      const compareItems = this.sortedRankings.filter(
        (x) => this.isCompare(x.rankingURL) && !this.isRating(x.rankingURL)
      );
      const pos = compareItems.findIndex((x) => x.rankingURL === url);

      if (pos >= 0 && pos < 3) return "hdr-green";
      if (pos >= 0 && pos >= compareTotal - 3) return "hdr-red";
      return "hdr-default";
    },
  },
};
</script>

<style scoped>
.chart-img {
  background: white;
  cursor: pointer;
}

/* Daily Trades Karten klickbar */
.daily-trade-card {
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.daily-trade-card:hover {
  background-color: rgba(255, 255, 255, 0.06);
}

.daily-trade-card.daily-trade-card-v2 {
  background-color: #263540;
  color: #fff;
  border-left: 3px solid #64b5f6;
}

.daily-trade-card.daily-trade-card-v2:hover {
  background-color: #304454;
}

/* Header Farben */
/* Classes are assigned dynamically by headerClass(). */
/*noinspection CssUnusedSymbol*/
.hdr-default {
  background: #1f1f1f;
  color: #fff;
}
/*noinspection CssUnusedSymbol*/
.hdr-green {
  background: #1b5e20;
  color: #fff;
}
/*noinspection CssUnusedSymbol*/
.hdr-red {
  background: #b71c1c;
  color: #fff;
}
</style>
